import { Component, inject, Input, OnDestroy, OnInit } from '@angular/core';
import { Portals } from '@qwyk/models';
import { CustomFieldsFacade } from '../../+state/custom-fields.facade';
import { AuthenticationFacade } from '@qwyk/portals/authentication';
import { Subject, combineLatest } from 'rxjs';
import { takeUntil, tap, filter } from 'rxjs/operators';
import {
    CustomFieldsEntity,
    CustomFieldType,
} from '../../+state/custom-fields.models';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { deepClone } from '@qwyk/core';

interface CustomFieldView extends CustomFieldsEntity {
    hidden: boolean;
}

interface StoredFieldsState {
    order: string[];
    hidden: string[];
}

@Component({
    selector: 'qwyk-custom-fields-view',
    templateUrl: './custom-fields-view.component.html',
    styleUrls: ['./custom-fields-view.component.scss'],
})
export class CustomFieldsViewComponent implements OnInit, OnDestroy {
    private readonly auth = inject(AuthenticationFacade);
    private readonly customFields = inject(CustomFieldsFacade);
    private readonly destroy$ = new Subject<void>();

    public dateFormat$ = this.auth.dateFormat$;
    public loading$ = this.customFields.loading$;
    public error$ = this.customFields.error$;

    public fields: CustomFieldView[] = [];
    public showHiddenFields = false;
    public hasHiddenFields = false;
    private currentUserId: string | null = null;
    private savedState: StoredFieldsState = { order: [], hidden: [] };

    private _parent!: Portals.ParentEntity;

    @Input()
    public set parent(parent: Portals.ParentEntity) {
        this._parent = parent;
        this.onParentChanged(parent);
    }

    ngOnInit(): void {
        combineLatest([this.customFields.allCustomFields$, this.auth.user$])
            .pipe(
                takeUntil(this.destroy$),
                filter(([_, user]) => !!user),
                tap(([customFields, user]) => {
                    this.currentUserId = user?.id || null;

                    if (customFields) {
                        const type =
                            customFields.length > 0
                                ? customFields[0].parent_type
                                : undefined;
                        this.savedState = this.loadState(type);

                        let newFields: CustomFieldView[] = customFields.map(
                            cf => ({
                                ...cf,
                                hidden: this.savedState.hidden.includes(cf.name),
                            })
                        );

                        newFields = this.applySavedOrder(
                            newFields,
                            this.savedState.order
                        );

                        this.fields = newFields;
                        this.updateHiddenStatus();
                    }
                })
            )
            .subscribe();
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    private onParentChanged(parent: Portals.ParentEntity) {
        this.customFields.init(parent, {});
    }

    public getFieldClass(field: CustomFieldView): string {
        if (field.type === CustomFieldType.BOOLEAN) {
            const value = field.value;
            const sValue = String(value).toLowerCase();

            if (sValue === 'true' || sValue === '1') {
                return 'positive';
            }
        }
        return 'neutral';
    }

    public toggleFieldVisibility(field: CustomFieldView, event: Event): void {
        event.stopPropagation();
        field.hidden = !field.hidden;
        this.updateHiddenStatus();
        this.saveState();
    }

    public toggleShowHidden(): void {
        this.showHiddenFields = !this.showHiddenFields;
    }

    public get hiddenFieldsCount(): number {
        return this.fields ? this.fields.filter(f => f.hidden).length : 0;
    }

    private updateHiddenStatus(): void {
        this.hasHiddenFields = this.fields.some(f => f.hidden);
    }

    public drop(event: CdkDragDrop<string[]>): void {
        moveItemInArray(this.fields, event.previousIndex, event.currentIndex);
        this.saveState();
    }

    private getStorageKey(internalType?: string): string | null {
        const type = internalType || this._parent?.type;
        const userSuffix = this.currentUserId ? `_${this.currentUserId}` : '';

        if (!type) {
            return null;
        }
        return `qwyk_custom_fields_state_${type}${userSuffix}`;
    }

    private saveState(): void {
        const type =
            this.fields.length > 0 ? this.fields[0].parent_type : undefined;
        const key = this.getStorageKey(type);
        if (!key) return;

        // Current fields shown on the screen
        const currentShownFieldNames = new Set(this.fields.map(f => f.name));

        // Let's build the new order
        // 1. First, take the current order of fields on the screen
        const newOrder = this.fields.map(f => f.name);

        // 2. Then, append the fields that were previously saved in the order
        // but are not currently present in the screen.
        this.savedState.order.forEach(savedFieldName => {
            if (!currentShownFieldNames.has(savedFieldName)) {
                newOrder.push(savedFieldName);
            }
        });

        // Let's build the new hidden list
        // 1. Current hidden fields on screen
        const currentlyHidden = this.fields.filter(f => f.hidden).map(f => f.name);
        const newHidden = [...currentlyHidden];

        // 2. Add previously hidden fields that are not present on screen right now
        this.savedState.hidden.forEach(savedHiddenName => {
            if (!currentShownFieldNames.has(savedHiddenName)) {
                newHidden.push(savedHiddenName);
            }
        });

        const stateToSave: StoredFieldsState = {
            order: newOrder,
            hidden: newHidden,
        };

        // Update the cached state to reflect what we are saving
        this.savedState = stateToSave;

        localStorage.setItem(key, JSON.stringify(stateToSave));
    }

    private loadState(type?: string): StoredFieldsState {
        const key = this.getStorageKey(type);
        if (!key) return { order: [], hidden: [] };

        const stored = localStorage.getItem(key);
        return stored ? JSON.parse(stored) : { order: [], hidden: [] };
    }

    private applySavedOrder(
        fields: CustomFieldView[],
        savedOrder: string[]
    ): CustomFieldView[] {
        if (!savedOrder || savedOrder.length === 0) {
            return fields;
        }

        const orderedFields: CustomFieldView[] = [];
        const fieldMap = new Map(fields.map(f => [f.name, f]));

        savedOrder.forEach(name => {
            const field = fieldMap.get(name);
            if (field) {
                orderedFields.push(field);
                fieldMap.delete(name);
            }
        });

        fieldMap.forEach(field => {
            orderedFields.push(field);
        });

        return orderedFields;
    }
}
